import APIClient from "web_modules/APIClient";
import { omit } from "lodash";
import { formatName, calculateDiscountAmount } from "@eave/shared/utils";

import { AppShape } from "@eave/shared/types/api";
import { VALoanAppShape } from "@eave/shared/types/api/va-loans";
import {
  VALoansPageData,
  addendumFormKeyToLabel,
  VALoansPageDataDisplay,
  VAPageSection,
} from "./types";
import {
  formatAddress,
  FlagstarAddressFormatted,
  AZAddressFormatted,
  phone,
} from "@eave/shared/config/contacts";
import {
  VASponsorAgentIDCode,
  VALenderMortgageeIDCode,
} from "@eave/shared/config/external-vendors";

export const fetchVAFields = async (appId: number) =>
  await APIClient.get<{ va_loan_application: VALoanAppShape }>(
    `/uw/loan_applications/${appId}/va_loan_application`,
  ).catch(() => {
    return { va_loan_application: null };
  });

/** Returns data required for the VA Loans Page
 * @param  {AppShape} app - The loan app
 * @returns VALoansPageData
 */
export const getVALoansPageData = async (app: AppShape): Promise<VALoansPageData> => {
  // Extract fields from the app
  const {
    active_rate,
    computed_properties: { loan_amount },
    decision: { effective_rate },
  } = app;

  // Extract fields from the va app back-end
  const { va_loan_application } = await fetchVAFields(app.id);

  return Promise.resolve({
    loan_application_id: app.id,
    external_id: app.external_id, // lender/mortgagee case no.

    borrower1_first_name: app.borrower1_first_name,
    borrower1_middle_name: app.borrower1_middle_name,
    borrower1_last_name: app.borrower1_last_name,

    borrower1_current_address_line1: app.borrower1_current_address_line1,
    borrower1_current_address_line2: app.borrower1_current_address_line2,
    borrower1_current_city: app.borrower1_current_city,
    borrower1_current_state: app.borrower1_current_state,
    borrower1_current_postal_code: app.borrower1_current_postal_code,

    has_borrower2: app.has_borrower2,

    purchased_property_address_line1: app.purchased_property_address_line1,
    purchased_property_address_line2: app.purchased_property_address_line2,
    purchased_property_city: app.purchased_property_city,
    purchased_property_state: app.purchased_property_state,
    purchased_property_postal_code: app.purchased_property_postal_code,

    loan_amount,
    effective_rate,
    proposed_maturity: "30 yrs.", // Hard-coded string is correct for now
    discount_amount: calculateDiscountAmount(active_rate, loan_amount),
    lender_mortgagee_id_code: VALenderMortgageeIDCode,
    sponsor_agent_id_code: VASponsorAgentIDCode,
    lender_mortgagee_name_and_address: AZAddressFormatted,
    name_of_sponsor_agent: "Flagstar Bank",
    address_of_sponsor_agent: FlagstarAddressFormatted,
    lender_mortgagee_telephone_number: phone.number,

    first_time_homebuyer: app.first_time_homebuyer,
    // names_and_functions_of_agents: `${TO_BE_ADDED} - Add LO Signature`,

    borrower1_is_veteran: app.borrower1_is_veteran,
    borrower2_is_veteran: app.borrower2_is_veteran,
    borrower1_use_va_entitlement: app.borrower1_use_va_entitlement,
    borrower2_use_va_entitlement: app.borrower2_use_va_entitlement,
    is_va_loan_application_borrower_value: app.is_va_loan_application_borrower_value,

    // Declarations - on va_loan_application
    va_loan_application_id: va_loan_application?.id,
    agency_case_number: va_loan_application?.agency_case_number,
    purpose_of_loan: va_loan_application?.purpose_of_loan,
    previous_va_home_loan: va_loan_application?.previous_va_home_loan,
    occupancy: va_loan_application?.occupancy,
    was_aware_of_lower_valuation: va_loan_application?.was_aware_of_lower_valuation,
    funding_fee_financing: va_loan_application?.funding_fee_financing,
    vesting_type: va_loan_application?.vesting_type,
    vesting_type_other_description: va_loan_application?.vesting_type_other_description,
  });
};

export const convertToDisplayValues = (data: VALoansPageData): VALoansPageDataDisplay => {
  const nameFields = ["borrower1_first_name", "borrower1_middle_name", "borrower1_last_name"];
  const addressFields = [
    "borrower1_current_address_line1",
    "borrower1_current_address_line2",
    "borrower1_current_city",
    "borrower1_current_state",
    "borrower1_current_postal_code",

    "purchased_property_address_line1",
    "purchased_property_address_line2",
    "purchased_property_city",
    "purchased_property_state",
    "purchased_property_postal_code",
  ];

  const diff = {
    ...data,
    borrower1_name: formatName(
      data.borrower1_first_name,
      data.borrower1_middle_name,
      data.borrower1_last_name,
    ),
    borrower1_current_address: formatAddress(
      data.borrower1_current_address_line1,
      data.borrower1_current_address_line2,
      data.borrower1_current_city,
      data.borrower1_current_state,
      data.borrower1_current_postal_code,
    ),
    purchased_property_address: formatAddress(
      data.purchased_property_address_line1,
      data.purchased_property_address_line2,
      data.purchased_property_city,
      data.purchased_property_state,
      data.purchased_property_postal_code,
    ),
  };

  return omit(diff, [...addressFields, ...nameFields]) as VALoansPageDataDisplay;
};

/** Groups & formats related data points and returns as a categorized object
 * @param  {VALoansPageData} pageData - A raw subset of data from the app
 * @param  {VALoanView} view - The form-based subset of the data that we want to display
 */
export const getPageSection = (
  unformattedData: VALoansPageData,
  section: VAPageSection,
): { key: string; label: string; value: any }[] => {
  if (!unformattedData) return [] as any;

  const data = convertToDisplayValues(unformattedData);

  // the provided key must be a valid key
  type keyInVAPageData = Partial<VALoansPageDataDisplay>;
  const field = (key: keyof keyInVAPageData) => ({
    key,
    label: getVALoanFormLabelFromKey(key),
    value: data[key],
  });

  const schema = {
    [VAPageSection.Form1802]: [
      field("agency_case_number"),
      field("purpose_of_loan"),
      field("previous_va_home_loan"),
      field("occupancy"),
      field("was_aware_of_lower_valuation"),
      field("funding_fee_financing"),
      field("vesting_type"),
      field("vesting_type_other_description"),
    ],
    [VAPageSection.ApplicationInfo]: [
      field("borrower1_is_veteran"),
      field("borrower2_is_veteran"),
      field("borrower1_use_va_entitlement"),
      field("borrower2_use_va_entitlement"),
      field("is_va_loan_application_borrower_value"),
    ],
    [VAPageSection.BorrowerInfo]: [
      field("borrower1_name"),
      field("borrower1_current_address"),
      field("purchased_property_address"),
    ],
    [VAPageSection.LoanInfo]: [
      field("loan_amount"),
      field("effective_rate"),
      field("proposed_maturity"),
      field("discount_amount"),
    ],
    [VAPageSection.LenderSponsorAgentInfo]: [
      field("lender_mortgagee_id_code"),
      field("sponsor_agent_id_code"),
      field("lender_mortgagee_name_and_address"),
      field("name_of_sponsor_agent"),
      field("address_of_sponsor_agent"),
      field("lender_mortgagee_telephone_number"),
      field("first_time_homebuyer"),
    ],
  };

  return schema[section];
};

const VALoanFormKeyToLabel: addendumFormKeyToLabel = {
  // Computed fields that are combined for display (like the borrower address)
  borrower1_name: "Borrower 1 Name",
  borrower1_current_address: "Borrower 1 Current Address",
  purchased_property_address: "Purchased Property Address",

  // TRUS-related VA Loans Values
  loan_application_id: "App ID",
  va_loan_application_id: "VA App ID",
  has_borrower2: "Has Borrower 2",
  loan_amount: "Loan Amount",
  effective_rate: "Rate (Effective Rate)",
  first_time_homebuyer: "Is first time homebuyer",
  vesting_type_other_description: "Vesting Type 'Other': Description",
  borrower1_is_veteran: "Is Borrower 1 a veteran?",
  borrower2_is_veteran: "Is Borrower 2 a veteran?",
  borrower1_use_va_entitlement: "Use Borrower 1's VA Entitlement?",
  borrower2_use_va_entitlement: "Use Borrower 2's VA Entitlement?",
  funding_fee_financing: "Did Borrower 1 finance the VA Funding Fee?",
  agency_case_number: "2. Agency Case No. (include any suffix)",
  external_id: "3. Lender/Mortgagee Case No.",
  proposed_maturity: "9. Proposed Maturity",
  discount_amount: "10. Discount Amount",
  lender_mortgagee_id_code: "13. Lender/Mortgagee ID Code",
  sponsor_agent_id_code: "14. Sponsor/Agent ID Code",
  lender_mortgagee_name_and_address: "15. Lender/Mortgagee Name and Address (incl zip)",
  name_of_sponsor_agent: "16 (1/2). Name of Sponsor/Agent",
  address_of_sponsor_agent: "16 (2/2). Address of Sponsor/Agent",
  lender_mortgagee_telephone_number: "17. Lender/Mortgagee Telephone Number",
  vesting_type: "19. Title Will Be Vested In (VA Only)",
  purpose_of_loan: "20. Purpose of Loan",
  // names_and_functions_of_agents: "21f. Names & Functions of Agents",
  previous_va_home_loan: "23. Have you ever had a VA Home Loan?",
  is_va_loan_application_borrower_value: "Is this a VA Loan Application?",
  was_aware_of_lower_valuation: "25(1). Were you aware of a lower valuation?",
  occupancy: "25(2). Occupancy",
};

// Given a form key, return the label for that form key
export const getVALoanFormLabelFromKey = (key: string): string => {
  if (!VALoanFormKeyToLabel[key]) throw new Error(`No label found for key: ${key}`);
  return VALoanFormKeyToLabel[key];
};
